import { useCallback, useEffect } from "react";
import { Cutting, CreateCutting, UpdateCutting } from "../types/models/Cutting.d";
import { UpdateEntityCutting } from "../types/models/Cutting";
import { useGetCuttingListQuery, useInsertCuttingMutation, useUpdateCuttingMutation, useDeleteCuttingMutation, } from "../apis/Cutting.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useCutting(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertCuttingMutation, { 
        isLoading: insertCuttingLoading, 
        isSuccess: insertCuttingIsSuccess,
        isError: insertCuttingIsError,
        error: insertCuttingError,
    }] = useInsertCuttingMutation()
    const [updateCuttingMutation, { 
        isLoading: updateCuttingLoading,
        isSuccess: updateCuttingIsSuccess,
        isError: updateCuttingIsError,
        error: updateCuttingError,
    }] = useUpdateCuttingMutation()
    const [deleteCuttingMutation, { 
        isLoading: deleteCuttingLoading,
        isSuccess: deleteCuttingIsSuccess,
        isError: deleteCuttingIsError,
        error: deleteCuttingError,
    }] = useDeleteCuttingMutation()

    const loading =
        insertCuttingLoading ||
        updateCuttingLoading ||
        deleteCuttingLoading ||
        false

    const insertCutting = useCallback(async (cutting: CreateCutting, redirectPath: string|undefined) => {
        try {
            const data = await insertCuttingMutation(cutting).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertCuttingMutation, navigate])
    
    useEffect(() => {
        if (insertCuttingIsSuccess) {
            openSuccessAlert('Cutting created')
        } else if (insertCuttingIsError) {
            console.log("add error", insertCuttingError)
            openFailAlert(t`Cutting add error: ` + insertCuttingError?.data?.message)
        }
    }, [
        insertCuttingIsSuccess,
        insertCuttingIsError,
        insertCuttingError
    ]);

    const updateCutting = useCallback(async (cutting: UpdateEntityCutting, redirectPath: string|undefined) => {
        try {
            const data = await updateCuttingMutation(cutting).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateCuttingMutation, navigate])
    
    useEffect(() => {
        if (updateCuttingIsSuccess) {
            openSuccessAlert(t`Cutting updated`)
        } else if (updateCuttingIsError) {
            console.log("update error", updateCuttingError)
            openFailAlert(t`Cutting update error: ` + updateCuttingError?.data?.message)
        }
    }, [
        updateCuttingIsSuccess,
        updateCuttingIsError,
        updateCuttingError
    ]);

    const deleteCutting = useCallback((cutting: Cutting, redirectPath: string|undefined) => {
        openDialog(async () => {
            cutting = {
                ...cutting,
            }
            try {
                const { data } = await deleteCuttingMutation(cutting);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteCuttingMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteCuttingIsSuccess) {
            openSuccessAlert(t`Cutting deleted`)
        } else if (deleteCuttingIsError) {
            console.log("delete error", deleteCuttingError)
            openFailAlert(t`Cutting delete error: ` + deleteCuttingError?.data?.message)
        }
    }, [
        deleteCuttingIsSuccess,
        deleteCuttingIsError,
        deleteCuttingError
    ]);

    return {
        loading,
        insertCutting,
        updateCutting,
        deleteCutting
    }
}
